import styled, { css } from 'styled-components/macro';

type ButtonIconStyledT = {
  color?: 'white' | 'darkBlue';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'contained' | 'outline' | 'transparent';
  disabled?: boolean;
};

const xs = css`
  padding: 0.25rem;
`;

const sm = css`
  padding: 0.375rem;
`;

const md = css`
  padding: 0.5rem;
`;

const lg = css`
  padding: 0.625rem;
`;

const xl = css`
  padding: 0.75rem;
`;

const white = css`
  background-color: ${({ theme }): string => theme.colors.surface};
`;

const darkBlue = css`
  background-color: ${({ theme }): string => theme.colors.primary.variant1};
`;

const whiteHover = css`
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }): string => theme.colors.secondary.variant5};
    }
  }
`;

const blueHover = css`
  @media (hover: hover) {
    &:hover {
      border-color: ${({ theme }): string => theme.colors.secondary.variant5};

      svg path {
        fill: ${({ theme }): string => theme.colors.secondary.variant5};
      }
    }
  }
`;

const disabledHover = css`
  @media (hover: hover) {
    &:hover {
      background-color: ${({ theme }): string => theme.colors.transparent};
    }
  }
`;

const contained = css<ButtonIconStyledT>`
  border: ${({ theme }): string => theme.borders.none};
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: ${({ theme }): string => theme.radius.md};

  ${({ color, disabled }): ReturnType<typeof css> => {
    if (color && color === 'white') {
      return blueHover;
    } else if (color && color === 'darkBlue') {
      return whiteHover;
    } else if (disabled) {
      return disabledHover;
    }

    return blueHover;
  }}
`;

const outline = css<ButtonIconStyledT>`
  border: ${({ theme, disabled }): string =>
    `${theme.borders.xs} ${
      disabled ? theme.colors.primary.variant7 : theme.colors.primary.variant6
    }`};
  background-color: ${({ theme }): string => theme.colors.surface};
  border-radius: ${({ theme }): string => theme.radius.md};

  ${({ color, disabled }): ReturnType<typeof css> => {
    if (color && color === 'white') {
      return blueHover;
    } else if (color && color === 'darkBlue') {
      return whiteHover;
    } else if (disabled) {
      return disabledHover;
    }

    return blueHover;
  }}
`;

const transparent = css<ButtonIconStyledT>`
  border: ${({ theme }): string => theme.borders.none};
  background-color: ${({ theme }): string => theme.colors.transparent};

  @media (hover: hover) {
    &:hover {
      border-radius: ${({ theme }): string => theme.radius.md};
      background-color: ${({ theme, disabled }): string =>
        disabled ? theme.colors.transparent : theme.colors.primary.variant6};
    }
  }
`;

const buttonSize = {
  xs,
  sm,
  md,
  lg,
  xl,
};

const buttonColor = {
  white,
  darkBlue,
};

const buttonVariant = {
  contained,
  outline,
  transparent,
};

export const ButtonIconStyled = styled.button<ButtonIconStyledT>`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  transition: 0.3s;

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  ${({ variant = 'contained' }): ReturnType<typeof css> => buttonVariant[variant]}
  ${({ size = 'md' }): ReturnType<typeof css> => buttonSize[size]}
  ${({ color }): ReturnType<typeof css> | string => (color ? buttonColor[color] : '')}
`;
